import { DatePicker, Input, InputNumber, Modal, Select, message } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { SearchStockWithCatalog } from "../../components/fin-web/SearchStockWithCatalog";
import type { StockSearchBrief } from "../../global/GlobalType";
import { UserHoldingsApi } from "./UserHoldingsApi";
import "./UserHoldingsPage.css";

const RECT_BTN = "user-holdings-rect-modal-btn";
const REASON_OPTS = ["技术面", "消息面", "基本面"];
const DEFAULT_BUY_SHARES = 10000;
const DEFAULT_REASON_TYPE = REASON_OPTS[0];

function ReasonDetailWithImport(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  rows?: number;
  placeholder?: string;
  stockCode?: string;
  onImportClick?: () => void;
}): JSX.Element {
  const {
    label,
    value,
    onChange,
    maxLength = 300,
    rows = 4,
    placeholder,
    stockCode,
    onImportClick,
  } = props;
  return (
    <div className="user-holding-form-block">
      <div className="uh-reason-detail-head">
        <span className="user-holding-lab">{label}</span>
        {stockCode && onImportClick ? (
          <button type="button" className="uh-reason-import-btn" onClick={onImportClick}>
            从笔记中导入
          </button>
        ) : null}
      </div>
      <Input.TextArea
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="user-holding-input"
      />
    </div>
  );
}

/** 建仓买入 */
export function OpenBuyModal(props: {
  spaceId: string;
  open: boolean;
  seedStock?: StockSearchBrief | null;
  /** 打开时预填成交价（如策略页当前股价） */
  seedBuyPrice?: number | null;
  onOpenNoteImport?: (stockCode: string, stockName: string, onPick: (content: string) => void) => void;
  onClose: (ok: boolean) => void;
  holdingsCount: number;
  /** 为 true 时不 toast「已建仓」（由调用方自定义成功反馈） */
  silentSuccess?: boolean;
}): JSX.Element {
  const {
    spaceId,
    open,
    seedStock,
    seedBuyPrice,
    onOpenNoteImport,
    onClose,
    holdingsCount,
    silentSuccess,
  } = props;
  const [picked, setPicked] = useState<StockSearchBrief | null>(null);
  const [buyPrice, setBuyPrice] = useState<number>();
  const [buyShares, setBuyShares] = useState<number | undefined>(DEFAULT_BUY_SHARES);
  const [reasonType, setReasonType] = useState(DEFAULT_REASON_TYPE);
  const [reasonDetail, setReasonDetail] = useState("");
  const [buyTime, setBuyTime] = useState<Dayjs | null>(dayjs());
  const wasOpenRef = useRef(false);

  // 仅在弹框从关闭→打开时初始化；避免父组件因 K 线按键重渲染导致 seedStock 引用变化而清空表单
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setBuyTime(dayjs());
      setBuyShares(DEFAULT_BUY_SHARES);
      setReasonDetail("");
      setReasonType(DEFAULT_REASON_TYPE);
      if (seedStock?.code) {
        setPicked(seedStock);
      } else {
        setPicked(null);
      }
      if (typeof seedBuyPrice === "number" && Number.isFinite(seedBuyPrice) && seedBuyPrice > 0) {
        setBuyPrice(seedBuyPrice);
      } else {
        setBuyPrice(undefined);
      }
    }
    wasOpenRef.current = open;
  }, [open, seedStock, seedBuyPrice]);

  // 弹框打开时截断所有按键，避免背后 K 线抢走快捷键
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => e.stopPropagation();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const ok = async () => {
    if (holdingsCount >= 50) {
      message.warning("最多只允许添加50只持仓中的股票哦！您可以先卖出后再添加新持仓");
      return;
    }
    if (!picked?.code) {
      message.error("请选择标的");
      return;
    }
    if (!buyShares || !(buyPrice && buyPrice > 0)) {
      message.error("请填写成交价与股数");
      return;
    }
    const bt = (buyTime ?? dayjs()).format("YYYY-MM-DD HH:mm:ss");
    const err = await UserHoldingsApi.openBuyTry(spaceId, {
      stockCode: picked.code,
      stockName: picked.name,
      buyPrice,
      buyShares,
      buyTime: bt,
      reasonType,
      reasonDetail,
    });
    if (!err) {
      if (!silentSuccess) {
        message.success("已建仓");
      }
      onClose(true);
      return;
    }
    if (String(err).includes("已在持仓中")) {
      message.warning("该股票已在持仓中，如需加仓请重试");
      return;
    }
    message.error(err);
  };

  return (
    <Modal
      open={open}
      title="建仓买入"
      onCancel={() => onClose(false)}
      width={620}
      okText="确认"
      cancelText="取消"
      okButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      onOk={() => void ok()}
      destroyOnClose
    >
      <div className="user-holding-form-block">
        <SearchStockWithCatalog
          stockCode={picked?.code}
          onSelect={(s) => setPicked(s)}
          onCleared={() => setPicked(null)}
        />
      </div>
      <div style={{ display: "flex", gap: 24, marginTop: 16 }}>
        <div className="user-holding-form-block" style={{ flex: 1 }}>
          <span className="user-holding-lab">成交价</span>
          <InputNumber
            className="user-holding-input"
            placeholder="成交价"
            style={{ width: "100%" }}
            value={buyPrice}
            onChange={(v) => setBuyPrice(typeof v === "number" ? v : undefined)}
            min={0}
            step={0.01}
            controls={false}
          />
        </div>
        <div className="user-holding-form-block" style={{ flex: 1 }}>
          <span className="user-holding-lab">股数</span>
          <InputNumber
            className="user-holding-input"
            placeholder="股数"
            style={{ width: "100%" }}
            value={buyShares}
            onChange={(v) => setBuyShares(typeof v === "number" ? v : undefined)}
            min={1}
            step={1}
            controls={false}
          />
        </div>
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <DatePicker
          className="user-holding-input user-holding-datepicker"
          showTime
          format="YYYY-MM-DD HH:mm:ss"
          style={{ width: "100%" }}
          value={buyTime}
          onChange={(d) => setBuyTime(d)}
          placeholder="买入时间，默认当前时刻，可改为历史成交时间"
          allowClear={false}
        />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">理由类别</span>
        <Select
          placeholder="请选择"
          options={REASON_OPTS.map((x) => ({ label: x, value: x }))}
          value={reasonType}
          onChange={setReasonType}
          style={{ width: "100%" }}
          allowClear={false}
        />
      </div>
      <ReasonDetailWithImport
        label="具体原因"
        placeholder="最多300字"
        value={reasonDetail}
        onChange={setReasonDetail}
        stockCode={picked?.code}
        onImportClick={
          picked?.code && onOpenNoteImport
            ? () =>
                onOpenNoteImport(picked.code, picked.name ?? "", (content) =>
                  setReasonDetail(content.slice(0, 300))
                )
            : undefined
        }
      />
    </Modal>
  );
}
